/**
 * GA4 analytics utility.
 *
 * Events are sent via window.gtag(), which is initialised by the Google Tag
 * base tag loaded through GTM (GTM-5BXKS4N3 → tag "Google Tag").
 *
 * All functions are safe to call before GTM loads — they queue silently
 * when window.gtag is not yet available.
 *
 * Event taxonomy follows GA4 recommended + custom naming conventions:
 *   - Recommended events keep their standard GA4 names (login, sign_up, purchase)
 *   - App-specific events are snake_case prefixed with domain context
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

// ─── Core helper ────────────────────────────────────────────────────────────

function gtag(command: string, ...args: unknown[]): void {
  if (typeof window === 'undefined') return
  // Ensure dataLayer exists even if GTM hasn't loaded yet
  window.dataLayer = window.dataLayer ?? []
  if (typeof window.gtag === 'function') {
    window.gtag(command, ...args)
  } else {
    // Fallback: push raw gtag command so it processes once gtag loads
    window.dataLayer.push([command, ...args])
  }
}

// ─── Auth events ────────────────────────────────────────────────────────────

/** User signs in to an existing account. */
export function trackLogin(method: 'email' | 'magic_link' | 'sso' = 'email') {
  gtag('event', 'login', { method })
}

/** New account created (pre-payment lead capture). */
export function trackSignUp(params: {
  method?: 'email'
  source?: string       // utm_source / referral source from signup form
}) {
  gtag('event', 'sign_up', {
    method: params.method ?? 'email',
    signup_source: params.source ?? 'direct',
  })
}

// ─── Purchase / subscription ─────────────────────────────────────────────────

/** Stripe subscription payment confirmed. */
export function trackPurchase(params: {
  transactionId?: string
  value?: number
  currency?: string
  planName?: string
}) {
  gtag('event', 'purchase', {
    transaction_id: params.transactionId ?? `txn_${Date.now()}`,
    value: params.value ?? 99,
    currency: params.currency ?? 'USD',
    items: [
      {
        item_id: 'estate_plan_annual',
        item_name: params.planName ?? 'End of an Era Annual Plan',
        price: params.value ?? 99,
        quantity: 1,
      },
    ],
  })
}

// ─── Estate planning (planner side) ──────────────────────────────────────────

/** A planning task/frame is completed. */
export function trackTaskComplete(taskId: string, taskName?: string) {
  gtag('event', 'estate_task_complete', {
    task_id: taskId,
    task_name: taskName ?? taskId,
  })
}

/** A planning flow section is fully completed. */
export function trackFlowComplete(flowId: string) {
  gtag('event', 'estate_flow_complete', {
    flow_id: flowId,
  })
}

/** A document is uploaded in the planning flow. */
export function trackDocumentUpload(params: {
  context: string    // e.g. 'will', 'trust', 'insurance', 'settlement'
  frameId?: string
}) {
  gtag('event', 'estate_document_upload', {
    upload_context: params.context,
    frame_id: params.frameId ?? 'unknown',
  })
}

// ─── Settlement (executor side) ──────────────────────────────────────────────

/** Executor begins the settlement workflow (post death-cert confirmation). */
export function trackSettlementStarted(estateId?: string) {
  gtag('event', 'settlement_started', {
    estate_id: estateId ? `estate_${estateId.slice(0, 8)}` : 'unknown', // partial ID only
  })
}

/** Death certificate is uploaded / submitted by executor. */
export function trackDeathCertUploaded() {
  gtag('event', 'death_cert_uploaded')
}

/** A settlement phase step is marked complete. */
export function trackSettlementStepComplete(params: {
  phase: string     // e.g. 'immediately-after-death', 'secure-assets', 'debts-taxes'
  stepId: string    // e.g. 'iad-1', 'dt-3'
  stepName?: string
}) {
  gtag('event', 'settlement_step_complete', {
    settlement_phase: params.phase,
    step_id: params.stepId,
    step_name: params.stepName ?? params.stepId,
  })
}

/** A full settlement phase (all steps) is completed. */
export function trackSettlementPhaseComplete(phase: string) {
  gtag('event', 'settlement_phase_complete', {
    settlement_phase: phase,
  })
}
